<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import BrandsServicesList from '@/components/BrandsServicesList.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const activeTab = ref(props?.data?.items[0]?.id);
watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});
const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>
<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      data?.background_color ? `bg-${data.background_color}` : '',
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        :class="[
          data?.background_color
            ? `brands-services brands-services--${data.background_color}`
            : 'brands-services',
        ]"
      >
        <h2
          v-if="data.title"
          class="mb-0 brands-services__title h2 text-center"
          :class="[data?.background_color === 'black' ? 'white' : 'black']"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data.title"
        ></h2>
        <Tabs
          v-model:value="activeTab"
          class="brands-services__tabs"
          :scrollable="true"
        >
          <TabList
            ref="tabList"
            :class="`brands-services__tabs-headers  tabs-bg-${data.background_color}`"
          >
            <Tab
              v-for="(tab, tabIndex) in data.items"
              :key="tab.id"
              :value="tab.id"
              class="brands-services__tabs-title"
              data-aos="fade-up"
              :data-aos-delay="tabIndex >= 1 ? 100 + 100 * tabIndex : 100"
            >
              {{ tab.title }}
            </Tab>
          </TabList>
          <TabPanels class="brands-services__tabs-content">
            <TabPanel v-for="tab in data.items" :key="tab.id" :value="tab.id">
              <BrandsServicesList
                :data="tab"
                :background-color="data?.background_color"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.brands-services {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 80px;
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &--white {
    #{$parent} {
      &__tabs-title {
        color: $on-surface-variant-2;
      }
    }
  }
  &--black {
    #{$parent} {
      &__tabs {
        &-title {
          color: $on-surface-variant-2;
          &[data-p='active'] {
            color: $white;
          }
        }
        &-headers {
          &:deep(.p-tablist-content) {
            border-bottom-color: $on-surface-variant-2;
          }
        }
      }
    }
  }
  &--gray {
    #{$parent} {
      &__tabs-title {
        color: $on-surface-variant-2;
      }
    }
  }
  &__title {
    margin: 0;
  }
  &__tabs {
    display: flex;
    flex-direction: column;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-content) {
        border-bottom: 1px solid $divider;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-bottom-width, 1);
        }
      }
      &:deep(.p-tablist-tab-list) {
        gap: 8px;
        @include media-breakpoint-up(sm) {
          justify-content: center;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }

      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      color: $on-surface-variant-2;
      cursor: pointer;
      position: relative;
      padding: 16px;
      text-align: center;
      transition: color 0.2s ease;
      white-space: unset;
      width: auto;
      @include media-breakpoint-up(sm) {
        padding: 17px 16px 15px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 17);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-bottom, 15);
      }
      &[data-p='active'] {
        color: $on-surface;
      }
    }
  }
}
</style>
